import { useState, useEffect } from "react";
import { restaurantAPI, aiAPI, upsellAPI, menuAPI } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  Building2, Clock, Phone, AlertTriangle, Save, Loader2, Plug, Sparkles,
  TrendingUp, Plus, Trash2, Zap
} from "lucide-react";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const TIMEZONES = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Phoenix"];

export default function SettingsPage() {
  const { isPremium } = useAuth();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Editable fields
  const [businessName, setBusinessName] = useState("");
  const [address, setAddress] = useState("");
  const [tz, setTz] = useState("");
  const [transferPhone, setTransferPhone] = useState("");
  const [greetingScript, setGreetingScript] = useState("");
  const [hours, setHours] = useState({});
  const [aiActive, setAiActive] = useState(false);

  // Danger zone
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);

  // Upsell
  const [upsellEnabled, setUpsellEnabled] = useState(false);
  const [upsellMaxPrompts, setUpsellMaxPrompts] = useState(2);
  const [upsellMappings, setUpsellMappings] = useState([]);
  const [upsellPriority, setUpsellPriority] = useState([]);
  const [upsellSaving, setUpsellSaving] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [newTrigger, setNewTrigger] = useState("");
  const [newSuggest, setNewSuggest] = useState("");
  const [newPriorityItem, setNewPriorityItem] = useState("");

  useEffect(() => {
    const fetchRestaurant = async () => {
      try {
        const [restRes, menuRes] = await Promise.all([
          restaurantAPI.get(),
          menuAPI.getItems(),
        ]);
        const r = restRes.data;
        setRestaurant(r);
        setBusinessName(r.business_name || "");
        setAddress(r.address || "");
        setTz(r.timezone || "");
        setTransferPhone(r.transfer_phone || "");
        setGreetingScript(r.greeting_script || "");
        setHours(r.hours || {});
        setAiActive(r.ai_active || false);

        // Upsell (premium only — may 403)
        try {
          const upsellRes = await upsellAPI.getConfig();
          const uc = upsellRes.data;
          setUpsellEnabled(uc.enabled || false);
          setUpsellMaxPrompts(uc.max_prompts_per_call || 2);
          setUpsellMappings(uc.item_mappings || []);
          setUpsellPriority(uc.priority_items || []);
        } catch { /* not premium */ }

        // Menu items for dropdowns
        setMenuItems(menuRes.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchRestaurant();
  }, []);

  const handleSaveBusinessInfo = async () => {
    setSaving(true);
    try {
      await restaurantAPI.updateSettings({ business_name: businessName, address, timezone: tz });
      toast.success("Business info updated");
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveHours = async () => {
    setSaving(true);
    try {
      await restaurantAPI.updateHours(hours);
      toast.success("Hours updated");
    } catch (err) {
      toast.error("Failed to save hours");
    } finally {
      setSaving(false);
    }
  };

  const handleSavePhone = async () => {
    setSaving(true);
    try {
      await restaurantAPI.updateSettings({ transfer_phone: transferPhone, greeting_script: greetingScript });
      toast.success("Phone settings updated");
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAI = async () => {
    const newState = !aiActive;
    try {
      await restaurantAPI.updateSettings({ ai_active: newState });
      setAiActive(newState);
      toast.success(newState ? "AI Agent activated" : "AI Agent paused");
    } catch (err) {
      toast.error("Failed to toggle AI");
    }
  };

  const updateDayHours = (day, field, value) => {
    setHours(prev => ({ ...prev, [day]: { ...prev[day], [field]: value } }));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-5 fade-in max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Settings</h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage your restaurant configuration</p>
      </div>

      {/* Business Info */}
      <Card data-testid="settings-business-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Building2 className="w-4 h-4" /> Business Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Business Name</Label>
            <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} data-testid="settings-business-name" />
          </div>
          <div className="space-y-2">
            <Label>Address</Label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} data-testid="settings-address" />
          </div>
          <div className="space-y-2">
            <Label>Timezone</Label>
            <Select value={tz} onValueChange={setTz}>
              <SelectTrigger data-testid="settings-timezone"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIMEZONES.map(t => <SelectItem key={t} value={t}>{t.replace("America/", "").replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleSaveBusinessInfo} disabled={saving} data-testid="save-business-info">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </CardContent>
      </Card>

      {/* Hours */}
      <Card data-testid="settings-hours-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Clock className="w-4 h-4" /> Hours of Operation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {DAYS.map(day => (
            <div key={day} className="flex items-center gap-4 py-1">
              <span className="w-24 text-sm font-medium capitalize">{day}</span>
              <Switch
                checked={!hours[day]?.closed}
                onCheckedChange={(v) => updateDayHours(day, "closed", !v)}
                data-testid={`settings-hours-${day}-toggle`}
              />
              <span className="text-xs text-muted-foreground w-10">{hours[day]?.closed ? "Closed" : "Open"}</span>
              {!hours[day]?.closed && (
                <>
                  <Input type="time" value={hours[day]?.open || ""} onChange={(e) => updateDayHours(day, "open", e.target.value)} className="w-28 h-8" data-testid={`settings-hours-${day}-open`} />
                  <span className="text-muted-foreground text-xs">to</span>
                  <Input type="time" value={hours[day]?.close || ""} onChange={(e) => updateDayHours(day, "close", e.target.value)} className="w-28 h-8" data-testid={`settings-hours-${day}-close`} />
                </>
              )}
            </div>
          ))}
          <Button onClick={handleSaveHours} disabled={saving} className="mt-3" data-testid="save-hours">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Hours
          </Button>
        </CardContent>
      </Card>

      {/* Phone Settings */}
      <Card data-testid="settings-phone-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Phone className="w-4 h-4" /> Phone Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">AI Phone Number</p>
            <p className="text-lg font-bold text-primary font-mono" data-testid="settings-ai-phone">{restaurant?.twilio_number || "Not provisioned"}</p>
          </div>
          <div className="space-y-2">
            <Label>Transfer Phone Number</Label>
            <Input value={transferPhone} onChange={(e) => setTransferPhone(e.target.value)} placeholder="+12125551234" data-testid="settings-transfer-phone" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Greeting Script</Label>
              <Button variant="outline" size="sm" className="h-7 text-[11px]"
                onClick={async () => {
                  try {
                    const res = await aiAPI.generateGreeting({ style: "friendly", cuisine_type: "Pizza" });
                    if (res.data?.greeting) {
                      setGreetingScript(res.data.greeting);
                      toast.success("AI greeting generated!");
                    }
                  } catch (e) { toast.error("Failed to generate greeting"); }
                }}
                data-testid="ai-generate-greeting-button"
              >
                <Sparkles className="w-3 h-3 mr-1 text-amber-500" /> AI Generate
              </Button>
            </div>
            <Textarea
              value={greetingScript}
              onChange={(e) => setGreetingScript(e.target.value)}
              rows={3}
              data-testid="settings-greeting-script"
            />
          </div>
          <Button onClick={handleSavePhone} disabled={saving} data-testid="save-phone-settings">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Phone Settings
          </Button>
        </CardContent>
      </Card>

      {/* Upsell Engine (Premium only) */}
      {isPremium && (
      <Card className="border-slate-100" data-testid="settings-upsell-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <TrendingUp className="w-4 h-4" /> Upsell Engine
              <Badge className="bg-orange-50 text-orange-600 border border-orange-200 badge-pill text-[10px] ml-1">Premium</Badge>
            </CardTitle>
            <Switch
              checked={upsellEnabled}
              onCheckedChange={(v) => setUpsellEnabled(v)}
              data-testid="upsell-toggle"
            />
          </div>
          <CardDescription>AI suggests add-ons during phone orders to increase average ticket size</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Max prompts */}
          <div className="space-y-2">
            <Label className="text-sm">Max upsell prompts per call</Label>
            <Select value={String(upsellMaxPrompts)} onValueChange={(v) => setUpsellMaxPrompts(parseInt(v))}>
              <SelectTrigger className="w-32 h-9" data-testid="upsell-max-prompts"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 prompt</SelectItem>
                <SelectItem value="2">2 prompts</SelectItem>
                <SelectItem value="3">3 prompts</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Separator />

          {/* Item-level mappings */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Item Upsell Mappings</Label>
              <span className="text-[11px] text-slate-400">{upsellMappings.length} rules</span>
            </div>
            <p className="text-[12px] text-slate-400">When a customer orders the trigger item, suggest the upsell item.</p>

            {upsellMappings.map((m, i) => (
              <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-100 bg-slate-50/50">
                <span className="text-[12px] text-slate-600 flex-1 truncate">{m.trigger_item}</span>
                <Zap className="w-3 h-3 text-orange-400 shrink-0" />
                <span className="text-[12px] text-slate-600 flex-1 truncate">{m.suggest_item}</span>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400 hover:text-red-500 shrink-0"
                  onClick={() => setUpsellMappings(prev => prev.filter((_, idx) => idx !== i))}
                  data-testid={`remove-mapping-${i}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))}

            <div className="flex gap-2">
              <Select value={newTrigger} onValueChange={setNewTrigger}>
                <SelectTrigger className="h-9 text-xs flex-1" data-testid="new-trigger-select"><SelectValue placeholder="When customer orders..." /></SelectTrigger>
                <SelectContent>{menuItems.map(i => <SelectItem key={i.id} value={i.name}>{i.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={newSuggest} onValueChange={setNewSuggest}>
                <SelectTrigger className="h-9 text-xs flex-1" data-testid="new-suggest-select"><SelectValue placeholder="Suggest this item..." /></SelectTrigger>
                <SelectContent>{menuItems.map(i => <SelectItem key={i.id} value={i.name}>{i.name}</SelectItem>)}</SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-9 shrink-0" data-testid="add-mapping-button"
                onClick={() => { if (newTrigger && newSuggest && newTrigger !== newSuggest) { setUpsellMappings(prev => [...prev, { trigger_item: newTrigger, suggest_item: newSuggest }]); setNewTrigger(""); setNewSuggest(""); } else { toast.error("Select two different items"); } }}>
                <Plus className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          <Separator />

          {/* Priority items */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">Fallback Priority Items</Label>
            <p className="text-[12px] text-slate-400">If no mapping matches, suggest these items in order.</p>

            <div className="flex flex-wrap gap-2">
              {upsellPriority.map((name, i) => (
                <Badge key={i} variant="outline" className="text-[11px] px-2.5 py-1 bg-white">
                  {name}
                  <button className="ml-1.5 text-slate-400 hover:text-red-500" onClick={() => setUpsellPriority(prev => prev.filter((_, idx) => idx !== i))}>
                    <Trash2 className="w-2.5 h-2.5" />
                  </button>
                </Badge>
              ))}
            </div>

            <div className="flex gap-2">
              <Select value={newPriorityItem} onValueChange={setNewPriorityItem}>
                <SelectTrigger className="h-9 text-xs flex-1" data-testid="new-priority-select"><SelectValue placeholder="Select item..." /></SelectTrigger>
                <SelectContent>{menuItems.filter(i => !upsellPriority.includes(i.name)).map(i => <SelectItem key={i.id} value={i.name}>{i.name}</SelectItem>)}</SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-9 shrink-0" data-testid="add-priority-button"
                onClick={() => { if (newPriorityItem) { setUpsellPriority(prev => [...prev, newPriorityItem]); setNewPriorityItem(""); } }}>
                <Plus className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          <Button onClick={async () => {
            setUpsellSaving(true);
            try {
              await upsellAPI.updateConfig({ enabled: upsellEnabled, max_prompts_per_call: upsellMaxPrompts, item_mappings: upsellMappings, priority_items: upsellPriority });
              toast.success("Upsell settings saved");
            } catch (err) { toast.error("Failed to save"); }
            finally { setUpsellSaving(false); }
          }} disabled={upsellSaving} data-testid="save-upsell-settings">
            {upsellSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Upsell Settings
          </Button>
        </CardContent>
      </Card>
      )}

      {/* Danger Zone */}
      <Card className="border-red-200" data-testid="settings-danger-zone">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base text-red-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <AlertTriangle className="w-4 h-4" /> Danger Zone
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg border border-red-100 bg-red-50/50">
            <div>
              <p className="text-sm font-medium">AI Agent Status</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {aiActive ? "AI is currently answering calls" : "AI is paused. Calls will go unanswered."}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={aiActive ? "bg-green-100 text-green-700 hover:bg-green-100" : "bg-red-100 text-red-700 hover:bg-red-100"} data-testid="ai-status-indicator">
                {aiActive ? "Active" : "Paused"}
              </Badge>
              <Button
                variant={aiActive ? "destructive" : "default"}
                size="sm"
                onClick={handleToggleAI}
                data-testid="toggle-ai-button"
              >
                {aiActive ? "Pause AI" : "Activate AI"}
              </Button>
            </div>
          </div>
          <div className="flex items-center justify-between p-4 rounded-lg border border-red-100 bg-red-50/50">
            <div>
              <p className="text-sm font-medium">POS Connection</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {restaurant?.pos_connected
                  ? `Connected to ${restaurant?.pos_type === "clover" ? "Clover" : "Toast"} POS`
                  : "Not connected"}
              </p>
            </div>
            <Badge className={restaurant?.pos_connected ? "bg-green-100 text-green-700 hover:bg-green-100" : "bg-red-100 text-red-700 hover:bg-red-100"} data-testid="pos-connection-status">
              <Plug className="w-3 h-3 mr-1" />
              {restaurant?.pos_connected ? `${(restaurant?.pos_type || "toast").charAt(0).toUpperCase() + (restaurant?.pos_type || "toast").slice(1)} Connected` : "Disconnected"}
            </Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
